import { useCallback, useEffect, useState } from 'react';

import formulaAdrogueMadiasPng from '@assets/formula_adrogue_madias.png';
import velocidadeCorrecaoPng from '@assets/velocidade_correcao.png';
import { Button } from '@components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import formatterDecimal from '@formatters/decimal';
import BigNumber from 'bignumber.js';
import { CircleHelp } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { z } from 'zod';

import { useDisturbiosSodio } from '../context';
import type { formSchema } from './formSchema';

function Hipernatremia() {
  const form = useFormContext<z.infer<typeof formSchema>>();
  const { serumSodiumFixed, water } = useDisturbiosSodio();

  const [solution, setSolution] = useState<number>(0.45);
  const [mEqL, setMEqL] = useState<number>(77);
  const [mEq, setMEq] = useState<number>(0);
  const [mEq24h, setMEq24h] = useState<number>(6);
  const [solution24h, setSolution24h] = useState<number>(0);
  const [velocity24h, setVelocity24h] = useState<number>(0);

  const handleChangeSolution = useCallback((value: string): void => {
    const valueParsed = Number(value.replace(',', '.'));
    setSolution(valueParsed);
    if (valueParsed === 0.45) {
      setMEqL(77);
    }
    if (valueParsed === 5) {
      setMEqL(0);
    }
  }, []);

  const calculateMEq = useCallback((): void => {
    // FÓRMULA: (mEq/L - Sódio Sérico Corrigido) / (Água corporal + 1)
    const serumSodiumFixedParsed = new BigNumber(serumSodiumFixed);
    const waterParsed = new BigNumber(water);
    const mEqLParsed = new BigNumber(mEqL);
    const step1 = mEqLParsed.minus(serumSodiumFixedParsed);
    const step2 = waterParsed.plus(1);
    const result = step1.dividedBy(step2);
    setMEq(result.toNumber() || 0);
  }, [mEqL, serumSodiumFixed, water]);

  useEffect(() => {
    calculateMEq();
  }, [calculateMEq]);

  const handleChangeMEq24h = useCallback((value: string): void => {
    const valueParsed = Number(value.replace(',', '.'));
    setMEq24h(valueParsed);
  }, []);

  const calculateSolution24h = useCallback((): void => {
    // FÓRMULA: (1000 x mEq24h) / mEq
    const mEqParsed = new BigNumber(mEq);
    const mEq24hParsed = new BigNumber(mEq24h);
    const step1 = mEq24hParsed.multipliedBy(1000);
    const result = step1.dividedBy(mEqParsed);
    setSolution24h(result.decimalPlaces(0).toNumber() || 0);
  }, [mEq, mEq24h]);

  useEffect(() => {
    calculateSolution24h();
  }, [calculateSolution24h]);

  const calculateVelocity24h = useCallback((): void => {
    // FÓRMULA: solution24h x 24
    const solution24hParsed = new BigNumber(solution24h);
    const result = solution24hParsed.dividedBy(24);
    setVelocity24h(result.decimalPlaces(1).toNumber() || 0);
  }, [solution24h]);

  useEffect(() => {
    calculateVelocity24h();
  }, [calculateVelocity24h]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'solution1' && value.solution1) {
        handleChangeSolution(value.solution1);
      }
      if (name === 'mEq24h' && value.mEq24h) {
        handleChangeMEq24h(value.mEq24h);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div className="flex flex-col items-center">
      <Label className="text-tertiary text-lg font-bold">Hipernatremia</Label>
      <Label className="mb-2 mt-6 self-start">1º - Escolha a solução:</Label>
      <FormField
        control={form.control}
        name="solution1"
        render={({ field }) => (
          <FormItem>
            <Select
              defaultValue={String(solution)}
              onValueChange={field.onChange}
              value={field.value}>
              <FormControl>
                <SelectTrigger onBlur={field.onBlur}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>

              <SelectContent>
                {[
                  {
                    label: 'NaCl 0,45%',
                    value: '0.45',
                  },
                  {
                    label: 'SG 5%',
                    value: '5',
                  },
                ].map((item) => (
                  <SelectItem
                    className="focus:bg-tertiary"
                    key={item.value}
                    value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      <Label className="mb-2 mt-6 self-start">
        2º - Como montar essa solução:
      </Label>
      {solution === 0.45 && (
        <div className="flex flex-col space-y-2">
          <Label className="text-tertiary">
            500mL SF 0,9% + 500mL SG 5% ou AD = 1000mL NaCl 0,45%
          </Label>
          <span className="text-foreground text-center text-sm">ou</span>
          <Label className="text-tertiary">
            250mL SF 0,9% + 250mL SG 5% ou AD = 500mL NaCl 0,45%
          </Label>
        </div>
      )}

      {solution === 5 && (
        <div className="flex flex-col space-y-2">
          <Label className="text-tertiary">
            500mL ou 1000mL de SG 5% (já vem pré-montado)
          </Label>
        </div>
      )}

      <Label className="mb-2 mt-6 self-start">
        3º - Neste caso, cada litro dessa solução reduz:
      </Label>
      <div className="flex flex-row items-center gap-1">
        <p className="text-tertiary text-lg font-semibold">
          {(mEq * -1).toFixed(1)}{' '}
          <span className="text-foreground text-sm font-light">
            mEq do paciente
          </span>
        </p>
        <Popover>
          <PopoverTrigger asChild>
            <Button className="cursor-pointer" size="icon" variant="ghost">
              <CircleHelp className="text-foreground" strokeWidth={2} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-lg space-y-4">
            <h1 className="text-secondary text-center font-medium">
              Fórmula de Adrogué-Madias
            </h1>
            <img
              alt="Fórmula de Adrogué-Madias"
              src={formulaAdrogueMadiasPng}
            />
          </PopoverContent>
        </Popover>
      </div>

      <Label className="mb-2 mt-6 self-start">
        4º - Mas quantos mEq quer reduzir em 24h?
      </Label>
      <FormField
        control={form.control}
        name="mEq24h"
        render={({ field: { ref, ...rest } }) => (
          <FormItem className="flex flex-row items-center gap-1">
            <FormControl>
              <NumberFormatBase
                className="[&_input]:w-36 [&_input]:text-center"
                customInput={Input}
                format={(value) => formatterDecimal(value, 1)}
                getCaretBoundary={(formattedValue) => {
                  const boundaryAry = Array.from({
                    length: formattedValue.length + 1,
                  }).map(() => true);
                  boundaryAry[0] = false;
                  boundaryAry[1] = false;
                  boundaryAry[2] = false;
                  return boundaryAry;
                }}
                getInputRef={ref}
                {...rest}
              />
            </FormControl>
            <FormLabel>
              <span className="text-foreground text-sm font-light">
                mEq em 24h
              </span>
            </FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-sm space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  Velocidade de correção
                </h1>
                <img alt="Velocidade de correção" src={velocidadeCorrecaoPng} />
              </PopoverContent>
            </Popover>
          </FormItem>
        )}
      />

      <Label className="mb-2 mt-6 self-start">
        5º - Nesse caso, você precisará de:
      </Label>
      <p className="text-tertiary text-lg font-semibold">
        {solution24h * -1}{' '}
        <span className="text-foreground text-sm font-light">
          mL dessa solução
        </span>
      </p>

      <Label className="mb-2 mt-6 self-start">6º - Prescrição:</Label>
      <p className="text-tertiary text-lg font-semibold">
        {solution24h * -1}{' '}
        <span className="text-foreground text-sm font-light">
          mL dessa solução IV em 24 horas
        </span>
      </p>

      <p className="text-tertiary text-lg font-semibold">
        {(velocity24h * -1).toFixed(1)}{' '}
        <span className="text-foreground text-sm font-light">
          mL/h (velocidade de infusão)
        </span>
      </p>

      <span className="text-foreground text-sm font-light">
        *Dosar o sódio sérico de maneira seriada para ajuste da infusão
      </span>
    </div>
  );
}

export default Hipernatremia;
